import { handleActions } from 'redux-actions';

import moment from 'moment';
import reject from 'lodash/reject';
import filter from 'lodash/filter';
import orderBy from 'lodash/orderBy';

import endpoints from '../../config/endpoints';

import http from '../shared/http';
import checkPermissions from '../shared/check-permissions';

import { createTableWithSorting } from '../shared/new-table';
import createFilters, { getFiltersObject } from '../shared/filters';
import createCalendar, { getDateRangeFromCalendar } from '../shared/calendar';
import createSwitchControlMulti from '../shared/switch-control/multi';
import createPagination from '../shared/new-pagination';

import { enum as affiliateRanks } from '../../../../../enums/affiliate-ranks';
import { enum as adminPermissions } from '../../../../../enums/admin-permissions';

import tableInitialData from './table-initial-data';

const namespace = 'ranksPointsReport';

const table = createTableWithSorting({
	namespace,
	initialState: {
		table: tableInitialData.table,
		sortedBy: tableInitialData.sortedBy
	},
	sortingConfig: {
		sort
	}
});

const pagination = createPagination({
	namespace,
	initialState: {
		perPage: 100
	}
});

const filters = createFilters({
	namespace,
	stateNodeName: 'filters',
	listOfFilters: [{
		name: 'affiliates',
		title: 'Affiliates',
		placeholder: 'Add affiliates',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.AFFILIATES
		}
	}]
});

const rankSwitch = createSwitchControlMulti({
	namespace,
	stateNodeName: 'rankSwitch',
	defaultValues: [
		{
			name: 'all',
			placeholder: 'All',
			active: true
		},
		...affiliateRanks.map((rank) => ({
			name: rank.id,
			placeholder: rank.name,
			active: false
		}))
	]
});

const calendar = createCalendar({
	namespace,
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(1, 'month').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

// action types
const FETCH_DATA_SUCCESS = `${namespace}/fetch-data-success`;
const FETCH_DATA_ERROR = `${namespace}/fetch-data-error`;
const TOGGLE_PROGRESS_BAR = `${namespace}/toggle-progress-bar`;
const LOCAL_SORT = `${namespace}/local-sort`;
const CLEAR = `${namespace}/clear`;

// permissions
export const allowedPermissions = {
	view: adminPermissions.ranksPointsReport
};

// initial state
const initialState = {
	data: null,
	table: table.initialState,
	pagination: pagination.initialState,
	filters: filters.initialState,
	calendar: calendar.initialState,
	rankSwitch: rankSwitch.initialState,
	showProgressBar: false,
	error: false
};

// actions
export const actions = {
	own: {
		fetchData: () => (dispatch, getState) => {
			dispatch(actions.own.fetch());
		},
		fetch: (page = 1) => (dispatch, getState) => {
			request(getRequestQuery(page, getState))(dispatch)
				.then((action) => {
					if (action.type === FETCH_DATA_SUCCESS) {
						dispatch(pagination.actions.update({
							page,
							total: action.payload.data.total
						}));
					}
				});
		},
		apply: () => (dispatch, getState) => {
			dispatch(actions.own.toggleProgressBar());
			dispatch(actions.own.fetch());
		},
		clear: () => (dispatch, getState) => {
			dispatch({ type: CLEAR });
		},
		toggleProgressBar: () => ({ type: TOGGLE_PROGRESS_BAR })
	},
	table: table.actions,
	pagination: {
		gotoPage: (page) => (dispatch, getState) => {
			dispatch(actions.own.toggleProgressBar());
			dispatch(actions.own.fetch(page));
		}
	},
	filters: filters.actions,
	calendar: calendar.actions,
	rankSwitch: rankSwitch.actions
};

const reducers = Object.assign(
	{
		[FETCH_DATA_SUCCESS]: (state, action) => {
			return {
				...state,
				data: formatReport(action.payload.data.rows),
				error: false,
				showProgressBar: false
			};
		},
		[FETCH_DATA_ERROR]: (state, action) => {
			return {
				...state,
				error: true,
				showProgressBar: false
			};
		},
		[TOGGLE_PROGRESS_BAR]: (state, action) => ({
			...state,
			showProgressBar: !state.showProgressBar
		}),
		[LOCAL_SORT]: (state, action) => ({
			...state,
			data: action.payload.rows
		}),
		[CLEAR]: (state, action) => initialState
	},
	table.reducers,
	pagination.reducers,
	filters.reducers,
	calendar.reducers,
	rankSwitch.reducers
);

// reducers
export default handleActions(reducers, initialState);

// selectors
export const selectors = {
	getData: state => state[namespace].data,
	getTable: table.selectors.getTable,
	getFilters: state => filters.selectors.getFilters(
		state => state[namespace].filters,
		state => state
	)(state),
	getCalendar: state => calendar.selectors.getCalendar(
		state => state[namespace].calendar,
		state => state
	)(state),
	getRankSwitch: state => ({
		filters: rankSwitch.selectors.getControls(state)
	}),
	getPagination: pagination.selectors.getPagination,
	getProgressBar: state => state[namespace].showProgressBar,
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state[namespace].error
};

// functions

function sort(remote) {
	return (dispatch, getState) => {
		if (!remote) {
			const state = getState()[namespace];
			const sorting = state.table.sortedBy.column;

			const relevantData = reject(state.data, [sorting.index, null]);
			const rejectedData = filter(state.data, [sorting.index, null]);

			dispatch({
				type: LOCAL_SORT,
				payload: {
					rows: [
						...orderBy(relevantData, sorting.index, sorting.direction),
						...rejectedData
					]
				}
			});
		}
	};
}

function toPercentage(numerator, denominator) {
	return denominator === 0 ? null : (numerator / denominator) * 100;
}

function formatReport(rows) {
	return rows.map((row) => ({
		id: row.affiliate.id,
		affiliate: `${row.affiliate.email}(${row.affiliate.id})`,
		rank: {
			curr: row.rank.curr,
			prev: row.rank.prev
		},
		conversions: {
			total: row.conversions.total,
			approved: row.conversions.approved,
			approvedPercent: toPercentage(
				row.conversions.approved,
				row.conversions.total
			),
			redeemedPercent: toPercentage(
				row.conversions.redeemed,
				row.conversions.approved
			)
		},
		balance: {
			curr: row.balance.curr,
			prev: row.balance.prev
		},
		purchases: {
			curr: {
				count: row.purchases.curr.count,
				value: row.purchases.curr.value
			},
			prev: {
				count: row.purchases.prev.count,
				value: row.purchases.prev.value
			}
		},
		desiredGood: {
			title: row.desiredGood && row.desiredGood.title,
			price: row.desiredGood && row.desiredGood.price
		}
	}));
}

function getRequestQuery(page, getState) {
	const state = getState()[namespace];

	const limit = state.pagination.perPage;
	const range = getDateRangeFromCalendar(state.calendar);
	const ranks = state.rankSwitch.filters.filter(f => f.active).map(f => f.name);
	const filters = getFiltersObject(state.filters, false);
	const inverse_filters = getFiltersObject(state.filters, true);

	return {
		offset: (page - 1) * limit,
		limit,
		dates: [range.start, range.end],
		ranks: ranks[0] === 'all' ? [] : ranks,
		filters,
		inverse_filters
	};
}

function request(query) {
	return http({
		url: endpoints.RANKS_POINTS_REPORT.BASE,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR],
		query
	});
}


// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/ranks-points-report/index.js